import React, { useEffect } from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { OvernightConfigForm } from '@/components/config_forms/overnight/OvernightConfigForm';
import type { OvernightConfig as TOvernightConfig } from '@/types/OvernightConfig';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getFieldErrorElement } from '@/utils/validation/helpers';
import { toast } from 'sonner';
import type { OvernightConfig as TOvernightConfigType } from '@/types';

interface ClientOvernightConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  noCard?: boolean;
  isCustomized?: boolean;
  onRevert?: () => void;
  granularCustomizedFields?: Record<string, boolean>;
  hideTitle?: boolean;
  hideToggle?: boolean;
}

export const ClientOvernightConfig: React.FC<ClientOvernightConfigProps> = ({
  formData,
  handleChange,
  onFieldBlur,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  noCard,
  isCustomized,
  granularCustomizedFields,
  onRevert,
  hideTitle,
  hideToggle,
}) => {
  const config: TOvernightConfig = {
    applyOvernightPremium: formData.apply_overnight_premium ?? false,
    startTime: formData.overnight_start_time ?? null,
    endTime: formData.overnight_end_time ?? null,
    overnightMethod: formData.overnight_premium_type as TOvernightConfig['overnightMethod'],
    thresholdEnabled: formData.overnight_threshold_enabled ?? false,
    thresholdType: formData.overnight_threshold_type as TOvernightConfig['thresholdType'],
    percentageThreshold: formData.overnight_percentage_threshold ?? null,
    hoursThreshold: formData.overnight_hours_threshold ?? null,
    premiumType: formData.overnight_hourly_premium_type as TOvernightConfig['premiumType'],
    premiumValue: formData.overnight_hourly_premium_value ?? null,
    flatRateAmount: formData.overnight_flat_rate_amount ?? null,
    timeCreditMinutes: formData.overnight_time_credit_minutes ?? null,
    premiumBasis: (formData.overnight_hourly_premium_basis ?? 'base_rate') as TOvernightConfig['premiumBasis'],
    applyOnHalfDays: formData.overnight_apply_on_half_days ?? true,
    // Unified rate-offset fields (Phase 9).
    floorSource: (((formData as Record<string, unknown>).overnight_floor_source) as TOvernightConfig['floorSource']) ?? 'none',
    overtimeOffset: ((formData as Record<string, unknown>).overnight_overtime_offset as number | undefined) ?? 0,
    floorTierCode: ((formData as Record<string, unknown>).overnight_floor_tier_code as string | null | undefined) ?? null,
    floorTierCustomValue: ((formData as Record<string, unknown>).overnight_floor_tier_custom_value as number | null | undefined) ?? null,
  };

  const overnightConfigCustomized: Record<string, boolean> = {
    applyOvernightPremium: granularCustomizedFields?.apply_overnight_premium ?? false,
    startTime: granularCustomizedFields?.overnight_start_time ?? false,
    endTime: granularCustomizedFields?.overnight_end_time ?? false,
    overnightMethod: granularCustomizedFields?.overnight_premium_type ?? false,
    thresholdEnabled: granularCustomizedFields?.overnight_threshold_enabled ?? false,
    thresholdType: granularCustomizedFields?.overnight_threshold_type ?? false,
    percentageThreshold: granularCustomizedFields?.overnight_percentage_threshold ?? false,
    hoursThreshold: granularCustomizedFields?.overnight_hours_threshold ?? false,
    premiumType: granularCustomizedFields?.overnight_hourly_premium_type ?? false,
    premiumValue: granularCustomizedFields?.overnight_hourly_premium_value ?? false,
    flatRateAmount: granularCustomizedFields?.overnight_flat_rate_amount ?? false,
    timeCreditMinutes: granularCustomizedFields?.overnight_time_credit_minutes ?? false,
    premiumBasis: granularCustomizedFields?.overnight_hourly_premium_basis ?? false,
    applyOnHalfDays: granularCustomizedFields?.overnight_apply_on_half_days ?? false,
  };

  const handleSwitchChange = (field: keyof ClientFormData, value: boolean) => {
    handleChange(field, value);
  };

  const onConfigChange = (updates: Partial<TOvernightConfig>) => {
    const newConfig = { ...config, ...updates };

    // When enabling threshold, default to a type if not set
    if (updates.thresholdEnabled && !newConfig.thresholdType) {
      newConfig.thresholdType = 'hours';
      handleChange('overnight_threshold_type', 'hours');
    }
    // When switching type, null out the other value
    if (updates.thresholdType === 'percentage') {
      newConfig.hoursThreshold = null;
      handleChange('overnight_hours_threshold', null);
    } else if (updates.thresholdType === 'hours') {
      newConfig.percentageThreshold = null;
      handleChange('overnight_percentage_threshold', null);
    }
    // When disabling threshold, null out both values and type
    if (updates.thresholdEnabled === false) {
      newConfig.thresholdType = null;
      handleChange('overnight_threshold_type', null);
      newConfig.percentageThreshold = null;
      handleChange('overnight_percentage_threshold', null);
      newConfig.hoursThreshold = null;
      handleChange('overnight_hours_threshold', null);
    }

    // When switching to hourly premium, default the premium type if not already set
    if (updates.overnightMethod === 'hourly_premium' && !config.premiumType) {
      handleChange('overnight_hourly_premium_type', 'fixed');
    }

    if (updates.applyOvernightPremium !== undefined) handleSwitchChange('apply_overnight_premium', updates.applyOvernightPremium);
    if (updates.startTime !== undefined) handleChange('overnight_start_time', updates.startTime);
    if (updates.endTime !== undefined) handleChange('overnight_end_time', updates.endTime);
    if (updates.overnightMethod !== undefined) handleChange('overnight_premium_type', updates.overnightMethod);
    if (updates.thresholdEnabled !== undefined) handleSwitchChange('overnight_threshold_enabled', updates.thresholdEnabled);
    if (updates.thresholdType !== undefined) handleChange('overnight_threshold_type', updates.thresholdType);
    if (updates.percentageThreshold !== undefined) handleChange('overnight_percentage_threshold', updates.percentageThreshold);
    if (updates.hoursThreshold !== undefined) handleChange('overnight_hours_threshold', updates.hoursThreshold);
    if (updates.premiumType !== undefined) handleChange('overnight_hourly_premium_type', updates.premiumType);
    if (updates.premiumValue !== undefined) handleChange('overnight_hourly_premium_value', updates.premiumValue);
    if (updates.premiumBasis !== undefined) handleChange('overnight_hourly_premium_basis', updates.premiumBasis);
    if (updates.flatRateAmount !== undefined) handleChange('overnight_flat_rate_amount', updates.flatRateAmount);
    if (updates.timeCreditMinutes !== undefined) handleChange('overnight_time_credit_minutes', updates.timeCreditMinutes);
    if (updates.applyOnHalfDays !== undefined) handleSwitchChange('overnight_apply_on_half_days', updates.applyOnHalfDays);
    // Unified rate-offset fields (Phase 9).
    if (updates.floorSource !== undefined) handleChange('overnight_floor_source' as keyof typeof formData, updates.floorSource);
    if (updates.overtimeOffset !== undefined) handleChange('overnight_overtime_offset' as keyof typeof formData, updates.overtimeOffset);
    if (updates.floorTierCode !== undefined) handleChange('overnight_floor_tier_code' as keyof typeof formData, updates.floorTierCode);
    if (updates.floorTierCustomValue !== undefined) handleChange('overnight_floor_tier_custom_value' as keyof typeof formData, updates.floorTierCustomValue);
  };

  const hasThresholdMultipliers = (formData.rate_multipliers || []).some(m => 
    m.code !== 'STRAIGHT_TIME' && 
    (m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null)
  );

  useEffect(() => {
    if (config.applyOvernightPremium && config.overnightMethod === 'offset_overtime' && !hasThresholdMultipliers) {
      onConfigChange({ overnightMethod: null });
      toast.info('Overnight premium method reset: "Offset Overtime" is unavailable without defined overtime thresholds.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onConfigChange is defined in render scope and would cause infinite loop
  }, [hasThresholdMultipliers, config.applyOvernightPremium, config.overnightMethod]);

  return (
    <OvernightConfigForm
      title={hideTitle ? "" : "Overnight Premium"}
      description={hideTitle ? "" : "Override default overnight settings for this client."}
      config={config}
      onConfigChange={onConfigChange}
      context="client"
      isCustomized={isCustomized}
      onRevert={onRevert}
      noCard={noCard}
      customizedFields={overnightConfigCustomized}
      startTimeProps={{
        value: config.startTime ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ startTime: e.target.value || null }),
        onBlur: () => onFieldBlur?.('overnight_start_time'),
        disabled: isLoading,
      }}
      startTimeError={getFieldErrorElement('overnight_start_time', errors, touchedFields)}
      endTimeProps={{
        value: config.endTime ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ endTime: e.target.value || null }),
        onBlur: () => onFieldBlur?.('overnight_end_time'),
        disabled: isLoading,
      }}
      endTimeError={getFieldErrorElement('overnight_end_time', errors, touchedFields)}
      premiumValueProps={{
        value: config.premiumValue ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ premiumValue: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('overnight_hourly_premium_value'),
        disabled: isLoading,
      }}
      premiumValueError={getFieldErrorElement('overnight_hourly_premium_value', errors, touchedFields)}
      flatRateAmountProps={{
        value: config.flatRateAmount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ flatRateAmount: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('overnight_flat_rate_amount'),
        disabled: isLoading,
      }}
      flatRateAmountError={getFieldErrorElement('overnight_flat_rate_amount', errors, touchedFields)}
      timeCreditMinutesProps={{
        value: config.timeCreditMinutes ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ timeCreditMinutes: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('overnight_time_credit_minutes'),
        disabled: isLoading,
      }}
      timeCreditMinutesError={getFieldErrorElement('overnight_time_credit_minutes', errors, touchedFields)}
      thresholdEnabled={config.thresholdEnabled}
      onThresholdEnabledChange={(checked) => onConfigChange({ thresholdEnabled: checked })}
      thresholdType={config.thresholdType}
      onThresholdTypeChange={(type) => onConfigChange({ thresholdType: type })}
      percentageThresholdProps={{
        value: config.percentageThreshold ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ percentageThreshold: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('overnight_percentage_threshold'),
        disabled: isLoading,
      }}
      percentageThresholdError={getFieldErrorElement('overnight_percentage_threshold', errors, touchedFields)}
      hoursThresholdProps={{
        value: config.hoursThreshold ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ hoursThreshold: e.target.value ? parseFloat(e.target.value) : null }),
        onBlur: () => onFieldBlur?.('overnight_hours_threshold'),
        disabled: isLoading,
      }}
      hoursThresholdError={getFieldErrorElement('overnight_hours_threshold', errors, touchedFields)}
      hasThresholdMultipliers={hasThresholdMultipliers}
      rateMultipliers={formData.rate_multipliers || []}
      roles={formData.roles || []}
      minimumBillableHours={formData.minimum_billable_hours}
      hideToggle={hideToggle}
      extendedOvertimeEnabled={(formData as Record<string, unknown>).overnight_extended_overtime_enabled as boolean ?? false}
      onExtendedOvertimeEnabledChange={(v) => handleChange('overnight_extended_overtime_enabled' as keyof typeof formData, v)}
      extendedOvertimeTiers={(formData as Record<string, unknown>).overnight_extended_overtime_tiers as TOvernightConfigType['extendedOvertimeTiers'] ?? []}
      onExtendedOvertimeTiersChange={(tiers) => handleChange('overnight_extended_overtime_tiers' as keyof typeof formData, tiers as unknown as ClientFormData[keyof ClientFormData])}
    />
  );
};
